/* eslint-disable max-lines */

import {makeAutoObservable, observable, runInAction} from 'mobx'
import {getIdWithPrefix, logger, Time} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  NumericIds,
  NumericIdsByProject,
  NumericIdTask,
  ProjectIds,
  ReverseNumericIds,
  ReverseNumericIdsByProject,
  ReverseProjectIds
} from 'yougile-model'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {Sync} from '@client/sync'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {checkAvailableUrl} from './check-available-url'
import {fetchIdProjects, FetchIdProjectsResult} from './fetch-id-projects'
import {fetchIdTasks, FetchIdTasksResult, getIdsUrl} from './fetch-id-tasks'
import {
  fetchReverseIdTasks,
  FetchReverseIdTasksResult
} from './fetch-reverse-id-tasks'
import {FetchWithDelay} from './fetch-with-delay'
import {reopenChat} from './reopen-chat'

interface State {
  modified: number
}
type RequestLimitGuard = {[taskIdOrIdTask: string]: number}

const maxRequestLimitPerId = 5

export class IdTasksStore {
  state: State = {modified: 0}
  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }
}

export class IdTasksApi {
  _isSupportedFeature = true
  _isSupportedFeatureRequested = false

  _ids: {[companyId: DataObjectId]: NumericIds} = {}
  _reverseIds: {[companyId: DataObjectId]: ReverseNumericIds} = {}

  _projectsIds: {[companyId: DataObjectId]: NumericIdsByProject} = {}
  _reverseProjectsIds: {[companyId: DataObjectId]: ReverseNumericIdsByProject} =
    {}

  _idsProject: {[companyId: DataObjectId]: ProjectIds} = {}
  _reserveIdsProject: {[companyId: DataObjectId]: ReverseProjectIds} = {}

  _requestLimitGuard: {[companyId: DataObjectId]: RequestLimitGuard} = {}

  timer: NodeJS.Timeout | undefined
  requestQueue: DataObjectId[] = []

  fetchIds: FetchWithDelay<FetchIdTasksResult | undefined>
  fetchReverseIds: FetchWithDelay<FetchReverseIdTasksResult | undefined>
  fetchReverseIdsForSearch: FetchWithDelay<
    FetchReverseIdTasksResult | undefined
  >
  fetchIdsProjects: FetchWithDelay<FetchIdProjectsResult | undefined>

  constructor() {
    this.fetchIds = new FetchWithDelay(
      fetchIdTasks,
      this.updateServerResults,
      (taskId: DataObjectId): boolean => {
        return !!this.getProjectIds()[taskId]
      }
    )
    this.fetchReverseIds = new FetchWithDelay(
      fetchReverseIdTasks,
      this.updateReverseServerResults
    )
    this.fetchReverseIdsForSearch = new FetchWithDelay(
      fetchReverseIdTasks,
      this.updateReverseServerResults
    )
    this.fetchIdsProjects = new FetchWithDelay(
      fetchIdProjects,
      this.updateProjectIdsServerResults,
      (id: DataObjectId): boolean => {
        return !!this.getIdsProject()[id]
      }
    )
    this.fetchReverseIds.delay = 500
    this.fetchReverseIdsForSearch.delay = 1000
    this.fetchIdsProjects.delay = 500
  }

  private getIds(): NumericIds {
    const companyId = globals.getCompanyId()
    return this._ids[companyId] || {}
  }
  private setIds(taskId: DataObjectId, idTask: NumericIdTask): void {
    const companyId = globals.getCompanyId()
    if (taskId && idTask && companyId) {
      this._ids[companyId] ??= {}
      this._ids[companyId][taskId] = idTask
    }
  }
  private getReverseIds(): ReverseNumericIds {
    const companyId = globals.getCompanyId()
    return this._reverseIds[companyId] || {}
  }
  private setReverseIds(idTask: string, taskId: DataObjectId): void {
    const companyId = globals.getCompanyId()
    if (idTask && taskId && companyId) {
      this._reverseIds[companyId] ??= {}
      this._reverseIds[companyId][idTask] = taskId
    }
  }

  getIdsProject(): ProjectIds {
    const companyId = globals.getCompanyId()
    return this._idsProject[companyId] || {}
  }
  setIdsProject(projectId: DataObjectId, idProject: string): void {
    const companyId = globals.getCompanyId()
    if (!projectId || !idProject || !companyId) {
      return
    }

    this._reserveIdsProject[companyId] ??= {}
    this._idsProject[companyId] ??= {}

    const prevValue = this._idsProject[companyId][projectId]
    delete this._reserveIdsProject[companyId][prevValue]

    this._reserveIdsProject[companyId][idProject] = projectId
    this._idsProject[companyId][projectId] = idProject
  }
  getReverseIdsProject(): ReverseProjectIds {
    const companyId = globals.getCompanyId()
    return this._reserveIdsProject[companyId] || {}
  }

  getProjectIds(): NumericIdsByProject {
    const companyId = globals.getCompanyId()
    return this._projectsIds[companyId] || {}
  }
  setProjectIds(
    projectId: DataObjectId,
    taskId: DataObjectId,
    num: NumericIdTask
  ): void {
    const companyId = globals.getCompanyId()
    if (projectId && num && companyId) {
      this._projectsIds[companyId] ??= {}
      this._projectsIds[companyId][taskId] = {id: num, projectId}
    }
  }
  getReverseProjectIds(): ReverseNumericIdsByProject {
    const companyId = globals.getCompanyId()
    return this._reverseProjectsIds[companyId] || {}
  }
  setReverseProjectIds(
    projectId: DataObjectId,
    idTask: string,
    taskId: DataObjectId
  ): void {
    const companyId = globals.getCompanyId()
    if (projectId && idTask && companyId) {
      this._reverseProjectsIds[companyId] ??= {}
      this._reverseProjectsIds[companyId][projectId] ??= {}
      this._reverseProjectsIds[companyId][projectId][idTask] = taskId
    }
  }

  // get ids

  public getIdTask(
    taskId: DataObjectId,
    onlyOldId = false
  ): NumericIdTask | undefined {
    if (!this.isSupported()) {
      return undefined
    }

    if (!taskId || !globals.getCompanyId()) {
      return undefined
    }

    const idTaskLocal = this.getIdTaskLocal(taskId, onlyOldId)
    if (idTaskLocal) {
      return idTaskLocal
    }

    if (!this.guard(taskId)) {
      return 'error'
    }

    this.fetchIds.request([taskId])

    return undefined
  }

  public makeRequest(taskId: DataObjectId | DataObjectId[]): void {
    const taskIds = Array.isArray(taskId) ? taskId : [taskId]
    this.fetchIds.request(taskIds)
  }

  private getIdTaskLocal(
    taskId: DataObjectId,
    onlyOldId = false
  ): NumericIdTask | undefined {
    const settings = CompanyPreferencesClient.get(globals.getCompanyId())

    if (!settings.useOldId && !onlyOldId) {
      const idTaskByProject = this.getProjectIds()[taskId]
      if (idTaskByProject) {
        const {id, projectId} = idTaskByProject
        const prefix = projectId && this.getIdsProject()[projectId]

        if (id && prefix) {
          return getIdWithPrefix(id, prefix)
        }
      }
    }

    const idTask = this.getIds()[taskId]
    if (idTask) {
      return getIdWithPrefix(idTask)
    }

    return undefined
  }

  private updateServerResults = (result?: FetchIdTasksResult): boolean => {
    let hasAnyChanges = false
    if (result?.numericIds) {
      const tasksIds = Object.keys(result.numericIds) as DataObjectId[]
      for (const taskId of tasksIds) {
        const idTask = result.numericIds[taskId]
        if (this.getIds()[taskId] !== idTask) {
          this.setIds(taskId, idTask)
          this.setReverseIds(idTask, taskId)
          hasAnyChanges = true
        }
      }
    }

    if (result?.numericIdsByProject) {
      const tasksIds = Object.keys(result.numericIdsByProject) as DataObjectId[]

      for (const taskId of tasksIds) {
        const {id, projectId} = result.numericIdsByProject[taskId]
        if (this.getProjectIds()?.[taskId]?.id !== id) {
          this.setProjectIds(projectId, taskId, id)
          this.setReverseProjectIds(projectId, id, taskId)
          hasAnyChanges = true
        }
      }
    }

    if (result?.projectIds) {
      const projects = Object.keys(result.projectIds) as DataObjectId[]
      for (const projectId of projects) {
        const idProject = result.projectIds[projectId]

        if (this.getIdProject(projectId) !== idProject) {
          this.setIdsProject(projectId, idProject)
          hasAnyChanges = true
        }
      }
    }

    if (hasAnyChanges) {
      runInAction(() => {
        idTasksStore.state.modified = Date.now()
        reopenChat()
      })
    }

    return hasAnyChanges
  }

  // get reverse ids

  public getReverseIdTask(
    idTask?: NumericIdTask,
    prefix = 'ID'
  ): DataObjectId | undefined {
    if (!this.isSupported()) {
      return undefined
    }

    prefix = prefix.toUpperCase()

    if (!idTask || !globals.getCompanyId()) {
      return undefined
    }

    const taskIdLocal = this.getReverseIdTaskLocal(idTask, prefix)
    if (taskIdLocal) {
      return taskIdLocal
    }

    const idTaskWithPrefix = getIdWithPrefix(idTask, prefix)
    this.fetchReverseIds.request([idTaskWithPrefix as DataObjectId], true)
    return undefined
  }

  public getReverseIdTaskForceAny(idTask?: NumericIdTask): DataObjectId[] {
    if (!this.isSupported() || !idTask) {
      return []
    }

    const output: DataObjectId[] = []
    const commonId = this.getReverseIds()[idTask]
    if (commonId && !this.getProjectIds()[commonId]?.id) {
      output.push(commonId)
    }

    const projectIds = Object.values(this.getReverseIdsProject() || {})
    for (const projectId of projectIds) {
      const taskId = this.getReverseProjectIds()[projectId]?.[idTask]
      if (taskId && !output.includes(taskId)) {
        output.push(taskId)
      }
    }

    const idTaskWithPrefix = getIdWithPrefix(idTask, '!ANY')

    this.fetchReverseIdsForSearch.request(
      [idTaskWithPrefix as DataObjectId],
      true
    )

    return output
  }

  public async getReverseIdTaskForce(
    idTask: NumericIdTask,
    prefix = 'ID'
  ): Promise<DataObjectId | undefined> {
    if (!this.isSupported()) {
      return undefined
    }

    prefix = prefix.toUpperCase()

    const taskIdLocal = this.getReverseIdTaskLocal(idTask, prefix)
    if (taskIdLocal) {
      return taskIdLocal
    }

    const idTaskWithPrefix = getIdWithPrefix(idTask, prefix)

    if (this.getRequestLimitGuard()[idTaskWithPrefix] >= maxRequestLimitPerId) {
      return undefined
    }

    this.incRequestLimitGuard(idTaskWithPrefix)
    const result = await fetchReverseIdTasks([idTaskWithPrefix])

    runInAction(() => {
      this.updateReverseServerResults(result)
    })

    if (prefix === 'ID') {
      const taskId = result?.reverseNumericIds?.[idTask]
      return taskId
    }

    if (result?.reverseNumericIdsByProject && result?.reverseProjectIds) {
      const projectId = result.reverseProjectIds[prefix]
      const taskId =
        projectId && result.reverseNumericIdsByProject[projectId]?.[idTask]
      return taskId
    }

    return undefined
  }

  private getReverseIdTaskLocal(
    idTask: NumericIdTask,
    value: string
  ): DataObjectId | undefined {
    const prefix = value.toUpperCase()
    if (prefix === 'ID') {
      const taskId = this.getReverseIds()[idTask]
      if (taskId) {
        return taskId
      }
    }

    const projectId = this.getReverseIdsProject()[prefix]
    if (projectId) {
      const taskId = this.getReverseProjectIds()[projectId]?.[idTask]
      if (taskId) {
        return taskId
      }
    }

    return undefined
  }

  private updateReverseServerResults = (
    result?: FetchReverseIdTasksResult
  ): boolean => {
    let hasAnyChanges = false
    if (result?.reverseNumericIds) {
      for (const idTask of Object.keys(result.reverseNumericIds)) {
        const taskId = result?.reverseNumericIds?.[idTask]
        if (taskId) {
          if (this.getIds()[taskId] !== idTask) {
            this.setIds(taskId, idTask)
            this.setReverseIds(idTask, taskId)
            hasAnyChanges = true
          }
        }
      }
    }

    if (result?.reverseNumericIdsByProject) {
      const revByProject = result.reverseNumericIdsByProject

      const projects = Object.keys(revByProject) as DataObjectId[]

      for (const projectId of projects) {
        const ids = revByProject[projectId]
        if (!ids) {
          continue
        }

        const idsTasks = Object.keys(ids)

        for (const idTask of idsTasks) {
          const taskId = ids[idTask]

          if (this.getProjectIds()?.[taskId]?.id !== idTask) {
            this.setProjectIds(projectId, taskId, idTask)
            this.setReverseProjectIds(projectId, idTask, taskId)
            hasAnyChanges = true
          }
        }
      }
    }

    if (result?.reverseProjectIds) {
      const idsProjects = Object.keys(result.reverseProjectIds)
      for (const idProject of idsProjects) {
        const projectId = result.reverseProjectIds[idProject]

        if (this.getIdProject(projectId) !== idProject) {
          this.setIdsProject(projectId, idProject)
          hasAnyChanges = true
        }
      }
    }

    if (hasAnyChanges) {
      runInAction(() => {
        idTasksStore.state.modified = Date.now()
      })
    }

    return hasAnyChanges
  }

  public getIdProject(projectId: DataObjectId): NumericIdTask | undefined {
    if (!this.isSupported()) {
      return undefined
    }

    if (!projectId || !globals.getCompanyId()) {
      return undefined
    }

    const id = this.getIdsProject()?.[projectId]
    if (id) {
      return id
    }

    this.fetchIdsProjects.request([projectId], true)

    return undefined
  }

  public setIdProject(projectId: DataObjectId, value: string): void {
    if (!this.isSupported()) {
      return undefined
    }

    this.setIdsProject(projectId, value)
  }

  public getReverseIdProject(prefix: string): NumericIdTask | undefined {
    if (!this.isSupported()) {
      return undefined
    }

    if (!prefix || !globals.getCompanyId()) {
      return undefined
    }

    const projectId = this.getReverseIdsProject()?.[prefix]
    if (projectId) {
      return projectId
    }

    return undefined
  }

  private updateProjectIdsServerResults = (
    result?: FetchIdProjectsResult
  ): boolean => {
    let hasAnyChanges = false

    if (result?.projectIds) {
      const projects = Object.keys(result.projectIds) as DataObjectId[]
      for (const projectId of projects) {
        const idProject = result.projectIds[projectId]

        this.setIdsProject(projectId, idProject)
        hasAnyChanges = true
      }
    }

    if (hasAnyChanges) {
      runInAction(() => (idTasksStore.state.modified = Date.now()))
    }

    return hasAnyChanges
  }

  public initBoard(board?: Hub): void {
    if (!this.isSupported()) {
      return undefined
    }

    if (!board) {
      return
    }

    runInAction(() => {
      const columns = Board.listColumns(board)
      const tasks = columns.map(col => Column.listTasks(col, false)).flat()

      this.fetchIds.request(tasks.map(t => t.id))

      const projects = Project.listProjects(globals.getCompanyId(), 'all', true)

      for (const project of projects) {
        idTasksApi.getIdProject(project.id)
      }
    })
  }

  private lastChecked = 0
  private checkTimeout = 3 * Time.minute

  private isSupported(): boolean {
    if (
      !this._isSupportedFeatureRequested &&
      Date.now() - this.lastChecked > this.checkTimeout
    ) {
      this.lastChecked = Date.now()
      void checkAvailableUrl(getIdsUrl).then(data => {
        this._isSupportedFeature = data
        this._isSupportedFeatureRequested = true
      })
    }

    return !!this._isSupportedFeature
  }

  private guard(taskId: DataObjectId): boolean {
    const box = Sync.getCurrentSandbox()
    const task = box.getHub(taskId)
    if (!task) {
      logger.warn(`Guard IdTask, taskId=${taskId} not found`)
      return false
    }

    if (box.getType(task) !== 'task') {
      logger.warn(`Guard IdTask, taskId=${taskId} is not a task`)
      return false
    }
    return true
  }

  private getRequestLimitGuard(): RequestLimitGuard {
    const companyId = globals.getCompanyId()
    return this._requestLimitGuard[companyId] || {}
  }

  private incRequestLimitGuard(taskIdOrIdTask: string): void {
    const companyId = globals.getCompanyId()
    if (taskIdOrIdTask && companyId) {
      this._requestLimitGuard[companyId] ??= {}
      const curr = this._requestLimitGuard[companyId][taskIdOrIdTask]

      if (!curr) {
        this._requestLimitGuard[companyId][taskIdOrIdTask] = 1
      } else {
        this._requestLimitGuard[companyId][taskIdOrIdTask] = curr + 1
      }
    }
  }

  //

  async getHub(idTask: string): Promise<Hub | undefined> {
    const [prefix, taskNum] = idTask.split('-')
    const taskId = await this.getReverseIdTaskForce(taskNum, prefix)
    if (taskId) {
      return Sync.getCurrentSandbox().getHub(taskId)
    }
    return undefined
  }
}

export const idTasksStore = new IdTasksStore()
export const idTasksApi = new IdTasksApi()
